import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import ScrollToTop from './components/ScrollToTop';
import NotificationManager from './components/common/NotificationManager';
import AnalyticsManager from './components/analytics/AnalyticsManager';
import CookieConsentBanner from './components/common/CookieConsentBanner';
import { CookieConsentProvider } from './contexts/CookieConsentContext';

// Import landing page components (unchanged)
import HomePage from './components/pages/HomePage';
import FeaturesPage from './components/pages/FeaturesPage';
import PricingPage from './components/pages/PricingPage';
import AboutPage from './components/pages/AboutPage';
import SupportPage from './components/pages/SupportPage';
import LoginPage from './components/pages/LoginPage';
import StaffLoginPage from './components/pages/StaffLoginPage';
import SignupPage from './components/pages/SignupPage';
import ForgotPasswordPage from './components/pages/ForgotPasswordPage';
import ResetPasswordPage from './components/pages/ResetPasswordPage';
import CheckoutSuccessPage from './components/pages/CheckoutSuccessPage';
import InvoiceLookup from './components/pages/InvoiceLookup';
import AcceptInvitationPage from './components/pages/AcceptInvitationPage';
import EmailVerificationPage from './components/pages/EmailVerificationPage';
import PrivacyPolicyPage from './components/pages/PrivacyPolicyPage';
import TermsOfUsePage from './components/pages/TermsOfUsePage';
import CookiePolicyPage from './components/pages/CookiePolicyPage';

// Import portal components
import PortalLayout from './components/layout/PortalLayout';
import StaffLayout from './components/layout/StaffLayout';
import { ResponsivePortalLayout, ResponsiveStaffLayout } from './components/layout/ResponsiveLayout';
import ClientDashboard from './components/portal/ClientDashboard';
import MobileClientDashboard from './components/portal/MobileClientDashboard';
import SendInvoice from './components/portal/SendInvoice';
import ReceivedInvoices from './components/portal/ReceivedInvoices';
import SentInvoices from './components/portal/SentInvoices';
import Chat from './components/portal/Chat';
import Payments from './components/portal/Payments';
import Settings from './components/portal/Settings';
import TodoList from './components/portal/TodoList';
import Contacts from './components/portal/Contacts';

// Import Stripe Connect components
import StripeConnectSetup from './components/stripe-connect/StripeConnectSetup';
import StripeConnectDashboard from './components/stripe-connect/StripeConnectDashboard';
import StaffDashboard from './components/staff/StaffDashboard';
import MobileStaffDashboard from './components/staff/MobileStaffDashboard';
// Import responsive staff components
import { 
  ResponsiveClientManagement,
  ResponsiveInvoiceOversight,
  ResponsiveStaffManagement,
  ResponsiveSecurityLogs,
  ResponsiveStaffSettings
} from './components/ResponsiveStaffComponents';
import ProtectedRoute from './components/auth/ProtectedRoute';
import GuestInvoicePage from './components/pages/GuestInvoicePage';
import ViewInvoiceRedirect from './components/pages/ViewInvoiceRedirect';
import { useIsMobile } from './hooks/useDeviceType';
import { ResponsiveClientDashboard, ResponsiveStaffDashboard } from './components/ResponsiveDashboard';
import {
  ResponsiveInvoices,
  ResponsiveToDo,
  ResponsiveContacts,
  ResponsivePayments,
  ResponsiveSendInvoice,
  ResponsiveChat,
  ResponsiveSettings,
  ResponsiveIntegrations
} from './components/ResponsiveComponents';

// Import subscription components
import SubscriptionPlans from './components/subscription/SubscriptionPlans';
import SubscriptionSuccess from './components/subscription/SubscriptionSuccess';
import SubscriptionCanceled from './components/subscription/SubscriptionCanceled';

// Import layout components (for landing pages only)
import Navigation from './components/layout/Navigation';
import Footer from './components/layout/Footer';
import AuthLayout from './components/layout/AuthLayout';

function App() {
  // Global token monitoring - track when token gets removed
  React.useEffect(() => {
    const originalRemoveItem = localStorage.removeItem;
    localStorage.removeItem = function(key, ...args) {
      if (key === 'token') {
        console.error('TOKEN REMOVED!', {
          key,
          stack: new Error().stack,
          timestamp: new Date().toISOString(),
          url: window.location.href
        });
      }
      return originalRemoveItem.call(this, key, ...args);
    };
    
    // Token monitoring enabled
  }, []);

  return (
    <HelmetProvider>
      <CookieConsentProvider>
        <AnalyticsManager />
        <Router>
          <ScrollToTop />
          <NotificationManager />
        <Routes>
        {/* Landing Pages - Public, with navbar and footer */}
        <Route path="/" element={
          <>
            <Navigation />
            <main>
              <HomePage />
            </main>
            <Footer />
          </>
        } />
        <Route path="/features" element={
          <>
            <Navigation />
            <main>
              <FeaturesPage />
            </main>
            <Footer />
          </>
        } />
        <Route path="/pricing" element={
          <>
            <Navigation />
            <main>
              <PricingPage />
            </main>
            <Footer />
          </>
        } />
        <Route path="/about" element={
          <>
            <Navigation />
            <main>
              <AboutPage />
            </main>
            <Footer />
          </>
        } />
        <Route path="/support" element={
          <>
            <Navigation />
            <main>
              <SupportPage />
            </main>
            <Footer />
          </>
        } />
        <Route element={<AuthLayout />}>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password-confirm" element={<ResetPasswordPage />} />
        </Route>
        <Route path="/staff-login" element={<StaffLoginPage />} />
        <Route path="/staff-portal" element={<Navigate to="/staff" replace />} />
        <Route path="/invoice/:token" element={<GuestInvoicePage />} />
        <Route path="/view-invoice/:invoiceId" element={<ViewInvoiceRedirect />} />
        <Route path="/lookup" element={<InvoiceLookup />} />
        <Route path="/accept-invite" element={
          <>
            <Navigation />
            <main>
              <AcceptInvitationPage />
            </main>
            <Footer />
          </>
        } />
        <Route path="/checkout-success" element={<CheckoutSuccessPage />} />
        <Route path="/verify-email" element={<EmailVerificationPage />} />
        <Route path="/privacy" element={
          <>
            <Navigation />
            <main>
              <PrivacyPolicyPage />
            </main>
            <Footer />
          </>
        } />
        <Route path="/terms" element={
          <>
            <Navigation />
            <main>
              <TermsOfUsePage />
            </main>
            <Footer />
          </>
        } />
        <Route path="/cookies" element={
          <>
            <Navigation />
            <main>
              <CookiePolicyPage />
            </main>
            <Footer />
          </>
        } />

        {/* Client Portal Routes - Protected, with responsive layout - Only for regular users */}
        <Route path="/portal" element={<ProtectedRoute requireRole={['main_user', 'additional_user', 'user']}><ResponsivePortalLayout /></ProtectedRoute>}>
          <Route index element={<Navigate to="dashboard" replace />} />
          <Route path="dashboard" element={<ResponsiveClientDashboard />} />
          <Route path="send-invoice" element={<ResponsiveSendInvoice />} />
          <Route path="invoices" element={<ResponsiveInvoices />} />
          <Route path="invoices/received" element={<ReceivedInvoices />} />
          <Route path="invoices/sent" element={<SentInvoices />} />
          <Route path="chat" element={<ResponsiveChat />} />
          <Route path="payments" element={<ResponsivePayments />} />
          <Route path="todo" element={<ResponsiveToDo />} />
          <Route path="contacts" element={<ResponsiveContacts />} />
          <Route path="integrations" element={
            <ProtectedRoute requireRole={['main_user', 'additional_user', 'user', 'admin']}>
              <ResponsiveIntegrations />
            </ProtectedRoute>
          } />
          <Route path="settings" element={
            <ProtectedRoute requireRole={['main_user', 'additional_user', 'user', 'admin']}>
              <ResponsiveSettings />
            </ProtectedRoute>
          } />
          <Route path="stripe-connect/setup" element={<StripeConnectSetup />} />
          <Route path="stripe-connect/dashboard" element={<StripeConnectDashboard />} />
        </Route>

        {/* Staff Portal Routes - Protected, with responsive layout - Only for admin/staff users */}
        <Route path="/staff" element={<ProtectedRoute requireRole={['admin', 'staff']}><ResponsiveStaffLayout /></ProtectedRoute>}>
          <Route index element={<Navigate to="dashboard" replace />} />
          <Route path="dashboard" element={<ResponsiveStaffDashboard />} />
          <Route path="manage-clients" element={<ResponsiveClientManagement />} />
          <Route path="invoices" element={<ResponsiveInvoiceOversight />} />
          <Route path="staff" element={<ResponsiveStaffManagement />} />
          <Route path="security" element={<ResponsiveSecurityLogs />} />
          <Route path="settings" element={<ResponsiveStaffSettings />} />
        </Route>

        {/* Subscription Routes - Public */}
        <Route path="/subscription" element={
          <>
            <Navigation />
            <main>
              <SubscriptionPlans />
            </main>
            <Footer />
          </>
        } />
        <Route path="/subscription/success" element={
          <>
            <Navigation />
            <main>
              <SubscriptionSuccess />
            </main>
            <Footer />
          </>
        } />
        <Route path="/subscription/canceled" element={
          <>
            <Navigation />
            <main>
              <SubscriptionCanceled />
            </main>
            <Footer />
          </>
        } />

        {/* Legacy dashboard route - redirect to appropriate portal */}
        <Route path="/dashboard" element={<ProtectedRoute><div>Redirecting...</div></ProtectedRoute>} />
        </Routes>
        </Router>
        <CookieConsentBanner />
      </CookieConsentProvider>
    </HelmetProvider>
  );
}

export default App;
